import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatMetric, formatMonthLabel, type MetricUnit } from './formatters';
import type { AreaMetric } from '../api/structure';

export function MetricsTable({ months, metrics }: { months: string[]; metrics: AreaMetric[] }) {
  return (
    <TableContainer component={Paper} sx={{ borderRadius: 3, maxHeight: 640 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Metric</TableCell>
            <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Type</TableCell>
            {months.map((mo) => (
              <TableCell key={mo} align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
                {formatMonthLabel(mo)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {metrics.map((m) => {
            const byMonth = new Map(m.series.map((p) => [p.month, p]));
            const rows: { key: 'actual' | 'plan' | 'growthPct'; label: string; unit?: MetricUnit }[] = [
              { key: 'actual', label: 'Actual' },
              ...(m.hasPlan ? [{ key: 'plan' as const, label: 'Plan' }] : []),
              ...(m.hasGrowthPct ? [{ key: 'growthPct' as const, label: 'Growth %', unit: 'percent' as MetricUnit }] : []),
            ];
            return rows.map((tr, idx) => (
              <TableRow key={`${m.subarea}::${m.metric}::${tr.key}`} hover>
                {idx === 0 && (
                  <TableCell rowSpan={rows.length} sx={{ verticalAlign: 'top', fontWeight: 500 }}>
                    {m.metric}
                    {m.subarea && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {m.subarea}
                      </Typography>
                    )}
                  </TableCell>
                )}
                <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{tr.label}</TableCell>
                {months.map((mo) => {
                  const point = byMonth.get(mo);
                  const value = point ? point[tr.key] : null;
                  const unit = tr.unit ?? m.unit;
                  return (
                    <TableCell key={mo} align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {value === null || value === undefined ? '—' : formatMetric(value, unit)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ));
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
